import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, MessageSquare, Send, X } from 'lucide-react';
import type { plans } from '@/lib/client';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useDocumentsStore } from '@/stores/documentsStore';
import { usePlanChatStore } from '@/stores/planChatStore';
import { Prose } from './Prose';

const EMPTY: plans.PlanChatMessage[] = [];

/** How often the page looks at a request read again, until its plan is made; the failed reads it takes before it stops. */
const FOLLOW_MS = 4000;
const FOLLOW_MISSES = 5;

/** The text of what the admin's page shows, without the chat itself: what the chat reads of their screen. */
function screenText(): string {
  const main = document.querySelector('main') ?? document.body;
  const copy = main.cloneNode(true) as HTMLElement;
  copy.querySelectorAll('[data-zac-chat]').forEach((node) => node.remove());
  return `Page: ${window.location.pathname}\n${(copy.textContent ?? '').replace(/\s+/g, ' ').trim()}`.slice(0, 3000);
}

/**
 * The chat about a plan (docs/plans/chat-on-document-pipeline.md, "The plan's chat"), opened from the bubble at the
 * bottom left: ask for a change in words and the plan takes it as its next version, or the request is read again when
 * the change is something new; ask about the plan or its run and ZAC answers from the steps, what the run said it did,
 * the browser panel's page and the text of this page. The history stays with the plan.
 */
export function PlanChat({ plan, onClose }: { plan: plans.PlanView; onClose: () => void }) {
  const navigate = useNavigate();
  const messages = usePlanChatStore((s) => s.messagesByPlan[plan.id] ?? EMPTY);
  const sending = usePlanChatStore((s) => s.sending[plan.id] ?? false);
  const error = usePlanChatStore((s) => s.error[plan.id] ?? null);
  const reread = usePlanChatStore((s) => s.rereadByPlan[plan.id]);
  const load = usePlanChatStore((s) => s.load);
  const send = usePlanChatStore((s) => s.send);
  const followed = usePlanChatStore((s) => s.followed);
  const kept = usePlanChatStore((s) => s.keptByPlan[plan.id] ?? true);
  // The plan's maker before its first message: nothing is kept yet, and the first message starts the conversation.
  const mine = useAuthStore((s) => s.user?.id === plan.created_by.id) && plan.conversation_id === undefined;
  const loadDocument = useDocumentsStore((s) => s.load);
  const [text, setText] = useState('');
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => { void load(plan.id); }, [plan.id, load]);
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [messages.length, sending]);

  // A request read again: follow its document to the plan that replaces this one, once (Back to this plan stays here),
  // and give up after a few reads that fail.
  useEffect(() => {
    if (reread === undefined) return;
    let stop = false;
    let misses = 0;
    const go = (to: string) => { followed(plan.id); navigate(to); };
    const look = async () => {
      const doc = await loadDocument(reread);
      if (stop) return;
      if (doc?.plan_id) return go(`/changes/${doc.plan_id}`);
      if (doc?.status === 'asking' || doc?.status === 'failed') return go(`/changes/documents/${reread}`);
      if (doc === null && ++misses >= FOLLOW_MISSES) return followed(plan.id);
      timer = window.setTimeout(() => void look(), FOLLOW_MS);
    };
    let timer = window.setTimeout(() => void look(), FOLLOW_MS);
    return () => { stop = true; window.clearTimeout(timer); };
  }, [reread, loadDocument, navigate, followed, plan.id]);

  const submit = async () => {
    if (sending || text.trim() === '') return;
    const sent = text;
    setText('');
    if (!(await send(plan.id, sent, screenText()))) setText(sent);
  };

  return (
    <aside data-zac-chat className="flex h-full min-h-0 flex-col rounded-2xl border border-border bg-card shadow-2xl">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground"><MessageSquare className="h-4 w-4" /> Ask ZAC about this plan</h2>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground" aria-label="Hide the chat"><X className="h-4 w-4" /></button>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {messages.length === 0 && !sending && (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>Ask for a change in your own words and ZAC updates the plan:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>"Make the max wait 3 minutes"</li>
              <li>"Don't record the calls"</li>
              <li>"Skip the holiday schedule"</li>
            </ul>
            <p>Something new, like another queue, has the request read again.</p>
            <p>Ask about the run too: "What is it doing now?", "Why is this step waiting?", "What's on the browser page?"</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
            {m.role === 'user'
              ? <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-primary/90 px-3 py-2 text-sm text-primary-foreground">{m.content}</div>
              : <div className="max-w-[95%] break-words rounded-2xl rounded-bl-sm border border-border bg-background px-3 py-2"><Prose>{m.content}</Prose></div>}
          </div>
        ))}
        {sending && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading your message…</div>}
        {reread !== undefined && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the request again. This page moves to the new plan once it's ready.</div>}
        <div ref={end} />
      </div>
      {!kept && !mine && <p className="mx-4 mb-2 text-xs text-muted-foreground">{plan.conversation_id !== undefined ? "Another person made this plan from their chat, so this conversation isn't saved." : "This plan's chat is kept for the person who made it, so this conversation isn't saved."}</p>}
      {error && <div className="mx-4 mb-2 rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1.5 text-xs text-red-300">{error}</div>}
      <div className="border-t border-border p-3">
        <div className="flex items-end gap-2 rounded-xl border border-border bg-background px-3 py-2 focus-within:ring-2 focus-within:ring-primary/40">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submit(); } }}
            rows={2}
            maxLength={2000}
            placeholder="Ask about the plan or its run, or ask for a change…"
            aria-label="Message about this plan"
            className="max-h-40 min-h-[2.5rem] flex-1 resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <button onClick={() => void submit()} disabled={sending || text.trim() === ''} className="rounded-lg bg-primary p-2 text-primary-foreground hover:bg-primary/90 disabled:opacity-40" aria-label="Send">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}

/**
 * The chat's bubble, at the bottom left of the plan's page in both of its views (the browser panel open or not): it
 * opens the chat as a window over the page, so the run and the browser stay in sight.
 */
export function PlanChatBubble({ plan }: { plan: plans.PlanView }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        data-zac-chat
        onClick={() => setOpen(true)}
        className="fixed bottom-6 left-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 lg:left-[15rem]"
        aria-label="Ask ZAC about this plan"
        title="Ask ZAC about this plan"
      >
        <MessageSquare className="h-5 w-5" />
      </button>
    );
  }
  return (
    <div data-zac-chat className="fixed inset-x-3 bottom-3 top-16 z-50 sm:inset-x-auto sm:left-6 sm:top-auto sm:h-[70vh] sm:w-[420px] lg:left-[15rem]">
      <PlanChat plan={plan} onClose={() => setOpen(false)} />
    </div>
  );
}
